import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  PlayCircleIcon,
  PauseCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from "@heroicons/react/24/outline";

import { ClosedCaptionIcon } from "lucide-react";

export default function EtVideoPanel2({
  videoId,
  className = "w-full rounded-xl overflow-hidden shadow-lg h-64 sm:h-80 md:h-[22rem] lg:h-[26rem]",
  autoPlay = false,
  startMuted = true,
  loop = true,
}) {

  // First Created - 04 Oct 2025
  // Author - Atul Pandey
  // Purpose - To display iframe videos like YouTube
  // Updated - 05 Oct 2025 - Refined the control icons and added step back
  // Updated - 05 Oct 2025 - Refined the controls added CC
  // Updated - 05 Oct 2025 - Refined the controls to be managed using keyboard shortcuts
  // Updated - 21 Nov 2025 - Refined to mask YT controls



  // ----------
  // 1. STATE
  // ----------
  const playerRef = useRef(null);
  const iframeElRef = useRef(null);

  const [playerReady, setPlayerReady] = useState(false);
  const [isMuted, setIsMuted] = useState(startMuted);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ccOn, setCcOn] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // NEW — poster overlay until play
  const [showPoster, setShowPoster] = useState(!autoPlay);
  const posterUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  // ----------
  // ELEMENT ID
  // ----------
  const elementId = useMemo(
    () => `et-video-${videoId}-${Math.random().toString(36).slice(2, 8)}`,
    [videoId]
  );

  // ----------
  // Get Player Instance
  // ----------
  const getPlayer = () => {
    const p = playerRef.current;
    return p && typeof p.getCurrentTime === "function" ? p : null;
  };

  // ----------
  // 2. LOAD YT API + INIT PLAYER
  // ----------
  useEffect(() => {
    const createPlayer = () => {
      const YTNS = window.YT;

      const instance = new YTNS.Player(elementId, {
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          showinfo: 0,
          iv_load_policy: 3,
          disablekb: 1,
          cc_load_policy: 1,
          cc_lang_pref: "en",
          hl: "en",
          loop: loop ? 1 : 0,
          playlist: loop ? videoId : undefined,
        },
        events: {
          onReady: (event) => {
            setIsLoading(false);
            setPlayerReady(true);
            playerRef.current = event.target;

            event.target.setPlaybackQuality("hd1080");
            if (startMuted) event.target.mute();
            else event.target.unMute();

            if (autoPlay) {
              event.target.playVideo();
            } else {
              event.target.cueVideoById(videoId);
            }

            iframeElRef.current = event.target.getIframe();
          },

          onStateChange: (event) => {
            const S = window.YT.PlayerState;

            if (event.data === S.PLAYING) {
              setShowPoster(false);
              setIsLoading(false);
              setIsPlaying(true);
            }
            if (event.data === S.PAUSED) {
              setIsPlaying(false);
            }
            if (event.data === S.ENDED && loop) {
              event.target.seekTo(0, true);
              event.target.playVideo();
            }
          },
        },
      });

      playerRef.current = instance;
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      if (!window.__ytApiLoading) {
        window.__ytApiLoading = true;
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }

      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        createPlayer();
      };
    }

    return () => {
      if (playerRef.current?.destroy) playerRef.current.destroy();
    };
  }, [elementId, autoPlay, startMuted, loop, videoId]);

  // ----------
  // 3. PROGRESS POLLING
  // ----------
  useEffect(() => {
    if (!playerReady) return;
    const interval = setInterval(() => {
      const p = getPlayer();
      if (!p) return;
      setProgress(p.getCurrentTime());
      setDuration(p.getDuration());
    }, 500);
    return () => clearInterval(interval);
  }, [playerReady]);

  // ----------
  // 4. CONTROLS
  // ----------
  const handleFullscreen = useCallback(() => {
    const el = iframeElRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
  }, []);

  const toggleMute = () => {
    const p = getPlayer(); if (!p) return;
    if (isMuted) p.unMute(); else p.mute();
    setIsMuted(!isMuted);
  };

  const togglePlayPause = () => {
    const p = getPlayer(); if (!p) return;
    if (isPlaying) p.pauseVideo(); else p.playVideo();
    setIsPlaying(!isPlaying);
  };

  const stepForward = () => {
    const p = getPlayer(); if (!p) return;
    p.seekTo(p.getCurrentTime() + 10, true);
  };

  const stepBackward = () => {
    const p = getPlayer(); if (!p) return;
    p.seekTo(Math.max(p.getCurrentTime() - 10, 0), true);
  };

  const handleSeek = (e) => {
    const p = getPlayer(); if (!p) return;
    const t = parseFloat(e.target.value);
    p.seekTo(t, true);
    setProgress(t);
  };

  const toggleCaptions = () => {
    const p = getPlayer(); if (!p) return;
    try {
      if (ccOn) {
        p.unloadModule?.("captions");
        p.setOption?.("captions", "track", {});
        setCcOn(false);
      } else {
        p.loadModule?.("captions");
        p.setOption?.("captions", "track", { languageCode: "en" });
        setCcOn(true);
      }
    } catch {}
  };

  // ----------
  // 5. KEYBOARD SHORTCUTS
  // ----------
  useEffect(() => {
    if (!playerReady) return;

    const handler = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      if (key === " " || key === "k") {
        e.preventDefault();
        togglePlayPause();
      }
      if (key === "m") toggleMute();
      if (key === "c") toggleCaptions();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [playerReady, isPlaying, isMuted, ccOn]);

  // ----------
  // 6. TIME FORMAT
  // ----------
  const formatTime = (sec) => {
    if (!sec || sec < 0) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const elapsedLabel = formatTime(progress);
  const totalLabel = formatTime(duration);

  // ----------
  // 7. RENDER
  // ----------
  return (
    <div className={`relative ${className}`}>

      {/* IFRAME */}
      <div id={elementId} className="absolute top-0 left-0 w-full h-full" />

      {/* POSTER OVERLAY */}
      {showPoster && (
        <div className="absolute inset-0 z-[20] flex items-center justify-center bg-black">
          <img
            src={posterUrl}
            className="w-full h-full object-cover opacity-70"
            alt="thumbnail"
          />

          <button
            onClick={() => {
              const p = getPlayer();
              if (p) {
                p.playVideo();
                setShowPoster(false);
              }
            }}
            className="absolute text-orange-500 hover:text-orange-400 translate-y-16 md:translate-y-24"
          >
            <PlayCircleIcon className="h-10 w-10 sm:h-16 sm:w-16 drop-shadow-2xl" />
          </button>
        </div>
      )}

      {/* GRADIENT MASK */}
      {!showPoster && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent pointer-events-none" />
      )}

      {/* LOADER */}
      {isLoading && (
        <div className="absolute inset-0 z-[25] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-20 h-20 rounded-full border-4 border-orange-500/40 border-t-orange-500 animate-spin-slow" />
        </div>
      )}

      {/* PROGRESS BAR */}
      {!showPoster && (
        <div className="absolute bottom-10 left-3 right-3 z-[30]">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-200 tabular-nums">{elapsedLabel}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.1"
              value={progress}
              onChange={handleSeek}
              className="flex-1 accent-orange-500 cursor-pointer h-1 rounded-lg bg-white/20"
            />
            <span className="text-xs text-gray-200 tabular-nums">{totalLabel}</span>
          </div>
        </div>
      )}

      {/* LEFT CONTROLS */}
      {!showPoster && (
        <div className="absolute bottom-3 left-3 z-[30] flex items-center gap-2 bg-black/60 rounded-lg px-3 py-1">
          <button onClick={togglePlayPause} className="text-orange-500 hover:text-orange-400">
            {isPlaying ? <PauseCircleIcon className="h-5 w-5" /> : <PlayCircleIcon className="h-5 w-5" />}
          </button>
          <button onClick={stepBackward} className="text-orange-500 hover:text-orange-400">
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          <button onClick={stepForward} className="text-orange-500 hover:text-orange-400">
            <ArrowUturnRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* RIGHT CONTROLS */}
      {!showPoster && (
        <div className="absolute bottom-3 right-3 z-[30] flex items-center gap-2 bg-black/60 rounded-lg px-3 py-1">
          <button
            onClick={toggleCaptions}
            className={`${ccOn ? "text-orange-500" : "text-gray-400"} hover:text-orange-400`}
          >
            <ClosedCaptionIcon className="h-5 w-5" />
          </button>
          <button onClick={toggleMute} className="text-orange-500 hover:text-orange-400">
            {isMuted ? <SpeakerXMarkIcon className="h-5 w-5" /> : <SpeakerWaveIcon className="h-5 w-5" />}
          </button>
          <button onClick={handleFullscreen} className="text-orange-500 hover:text-orange-400">
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </button>
        </div>
      )}

    </div>
  );
}